import { Injectable, TemplateRef } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { NbModalBase } from '@app/common/basic/models/nb-modal-base';
import interact from 'interactjs';
import { defaults } from 'lodash';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

export interface IModalOptions<T> extends ModalOptions<T> {
  template: any;
  draggable?: boolean;
  resizable?: boolean;
}

@Injectable()
export class ModalService {
  constructor(private bsModalService: BsModalService, private router: Router) {
    this.router.events.subscribe(val => {
      if (val instanceof NavigationStart) {
        this.bsModalService.hide(1); // prevent modal from opening when navigated to new page
      }
    });
  }
  defaultOptions: IModalOptions<any> = {
    backdrop: 'static',
    draggable: true,
    resizable: false,
    // TODO: investigate the modal performance issue when animated set to true
    animated: false,
    template: '',
  };

  static makeModalResizable(modalDialog: HTMLElement) {
    const modalContent = this.getModalContent(modalDialog);
    const ele = interact(modalContent);
    ele
      .resizable({ edges: { right: true, bottom: true } })
      .on(['resizestart', 'resizeend', 'resizemove'], (e: MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
      })
      .on('resizemove', e => {
        const target = e.target as HTMLElement;
        const rectWidth = e.rect.width;
        const rectHeight = e.rect.height;

        // update the element's style
        target.style.width = `${rectWidth}px`;
        target.style.height = `${rectHeight}px`;
      });
  }

  static makeModalDraggable(modalDialog: HTMLElement) {
    const ele = interact(modalDialog);
    const modalContent = this.getModalContent(modalDialog);
    const modalHeader = modalDialog.querySelector<HTMLElement>('.modal-header');
    modalHeader.style.cursor = 'move';
    ele
      .draggable({
        allowFrom: '.modal-header',
      })
      .on('dragmove', event => {
        const target = event.target as HTMLElement;
        // keep the dragged position in the data-x/data-y attributes
        const x = (parseFloat(target.getAttribute('data-x')) || 0) + event.dx;
        const y = (parseFloat(target.getAttribute('data-y')) || 0) + event.dy;

        // translate the element
        modalContent.style.transform = `translate(${x}px, ${y}px)`;

        // update the position attributes
        target.setAttribute('data-x', x);
        target.setAttribute('data-y', y);
      });
  }

  private static getModalContent(modalDialog: HTMLElement) {
    return modalDialog.querySelector<HTMLElement>('.modal-content');
  }

  open<T = any>(opts: IModalOptions<string | TemplateRef<any> | { new (...args: any[]): T }>): Observable<T> {
    if (!opts.template || opts.template.prototype instanceof NbModalBase !== true) {
      throw new Error('All modal components should inherit from NbModalBase class');
    }

    const options = defaults(opts, this.defaultOptions);
    const ref = this.bsModalService.show(options.template, options);
    const modalBase = ref.content as NbModalBase;
    modalBase.modalRef = ref;

    setTimeout(() => {
      const nodes = document.querySelectorAll<HTMLElement>('.modal-dialog');
      const modalDialog = nodes[nodes.length - 1];
      const modalContentRef = ModalService.getModalContent(modalDialog);
      modalContentRef.style.display = 'flex';
      modalContentRef.style.flexDirection = 'column';
      modalContentRef.style.width = '100%';
      modalContentRef.style.height = '100%';

      if (options.draggable) {
        ModalService.makeModalDraggable(modalDialog);
      }

      if (options.resizable) {
        ModalService.makeModalResizable(modalDialog);
      }
    }, 100);

    return modalBase.modalResult.pipe(take(1));
  }
}
